import { Box, Button, Card, CardContent, FormControlLabel, IconButton, InputAdornment, Switch, TextField, Tooltip } from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useMemo, useState } from "react";
import { financeApi } from "../lib/api";
import { AccountSelect } from "../components/FinanceSelects";
import { EditPanelActions } from "../components/EditPanelActions";
import { SidePanel } from "../components/SidePanel";
import { useBlockedAccessGuard } from "../hooks/useBlockedAccessGuard";
import { currencySymbol, money } from "../lib/format";
import { queryKeys } from "../lib/queryKeys";
import type { Account, Group, TransferLinkRule, TransferLinkSuggestion, TransactionLinkedLeg } from "../lib/types";

type LinkedTransactionsPageProps = {
  group: Group;
  onBlockedAccess?: () => void;
};

type RuleModalState = { kind: "closed" } | { kind: "create" } | { kind: "edit"; rule: TransferLinkRule };

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function validateRegex(value: string): string | undefined {
  if (!value.trim()) return undefined;
  try {
    new RegExp(value);
    return undefined;
  } catch (error) {
    return error instanceof Error ? error.message : "Invalid regular expression.";
  }
}

function formatLegDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function LegSummary({ leg, accountName }: { leg: TransactionLinkedLeg; accountName: string }) {
  const outgoing = leg.type === "expense" || leg.type === "transfer";
  return (
    <Box className="linked-leg">
      <strong className="linked-leg-title">{leg.merchant || leg.note || "Record"}</strong>
      <span className="linked-leg-meta">
        {accountName} · {formatLegDate(leg.occurred_at)} ·{" "}
        <span className={outgoing ? "amount-expense" : "amount-income"}>
          {outgoing ? "−" : "+"}
          {currencySymbol(leg.currency)}
          {money(leg.amount)}
        </span>
      </span>
    </Box>
  );
}

// Nested side panel: search a chosen account's records and insert the picked one's text.
function RecordPatternPicker({
  group,
  accountId,
  accountName,
  onPick,
  onBack,
  onCloseAll,
  open,
}: {
  group: Group;
  accountId: string;
  accountName: string;
  onPick: (text: string) => void;
  onBack: () => void;
  onCloseAll: () => void;
  open?: boolean;
}) {
  const [search, setSearch] = useState("");
  const recordsQuery = useQuery({
    queryKey: ["linked-record-picker", group.id, accountId, search],
    queryFn: () => financeApi.transactionsPage(group.id, { accountId, search, limit: 25, offset: 0 }),
    enabled: Boolean(accountId),
  });
  // The API's account filter also matches transfers pointing at the account; keep only
  // records whose own account is the chosen one.
  const records = (recordsQuery.data?.items || []).filter((record) => record.account_id === accountId);

  return (
    <SidePanel ariaLabelledBy="recordPickerTitle" className="linked-picker-modal" open={open} onClose={onBack}>
      {() => (
        <>
          <Box className="records-modal-toolbar">
            <IconButton className="records-icon-button" type="button" aria-label="Back to rule" title="Back" onClick={onBack}>
              <ArrowBackOutlinedIcon fontSize="small" />
            </IconButton>
            <h2 id="recordPickerTitle">Pick a record from {accountName || "account"}</h2>
            <IconButton className="records-icon-button" type="button" aria-label="Close" title="Close" onClick={onCloseAll}>
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </IconButton>
          </Box>
          <Box className="linked-picker-body">
            <TextField
              fullWidth
              size="small"
              placeholder="Search merchant or note"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchOutlinedIcon fontSize="small" /></InputAdornment> } }}
            />
            <p className="linked-picker-hint">Tap a record to insert its text into the field.</p>
            {recordsQuery.isLoading ? <p className="status">Loading records…</p> : null}
            {!recordsQuery.isLoading && !records.length ? <p className="status">No matching records.</p> : null}
            <Box className="linked-picker-list">
              {records.map((record) => {
                const text = record.merchant || record.note || "";
                const outgoing = record.type === "expense" || record.type === "transfer";
                return (
                  <button
                    key={record.id}
                    type="button"
                    className="linked-picker-item"
                    disabled={!text}
                    onClick={() => {
                      onPick(text);
                      onBack();
                    }}
                  >
                    <span className="linked-picker-item-main">
                      <strong>{record.merchant || record.note || "(no text)"}</strong>
                      <span>{formatLegDate(record.occurred_at)}</span>
                    </span>
                    <span className={outgoing ? "amount-expense" : "amount-income"}>
                      {outgoing ? "−" : "+"}
                      {currencySymbol(record.currency)}
                      {money(record.amount)}
                    </span>
                  </button>
                );
              })}
            </Box>
          </Box>
        </>
      )}
    </SidePanel>
  );
}

function RuleModal({
  state,
  group,
  accounts,
  open,
  onClose,
  onDeleted,
}: {
  state: Exclude<RuleModalState, { kind: "closed" }>;
  group: Group;
  accounts: Account[];
  open?: boolean;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const rule = state.kind === "edit" ? state.rule : null;
  const membersQuery = useQuery({ queryKey: queryKeys.members(group.id), queryFn: () => financeApi.members(group.id) });
  const members = membersQuery.data || [];
  const [sourceAccountId, setSourceAccountId] = useState(rule?.source_account_id || "");
  const [destAccountId, setDestAccountId] = useState(rule?.dest_account_id || "");
  const [sourcePattern, setSourcePattern] = useState(rule?.source_pattern || "");
  const [destPattern, setDestPattern] = useState(rule?.dest_pattern || "");
  const [sourceIsRegex, setSourceIsRegex] = useState(rule?.source_pattern_is_regex ?? false);
  const [destIsRegex, setDestIsRegex] = useState(rule?.dest_pattern_is_regex ?? false);
  const [autoLink, setAutoLink] = useState(rule?.auto_link ?? false);
  const [dateWindow, setDateWindow] = useState(String(rule?.date_window_days ?? 1));
  const [status, setStatus] = useState("");
  const [picker, setPicker] = useState<null | "source" | "dest">(null);

  const activeAccounts = accounts.filter((account) => !account.is_archived);
  const accountName = (id: string) => accounts.find((account) => account.id === id)?.name || "account";
  const sourceRegexError = sourceIsRegex ? validateRegex(sourcePattern) : undefined;
  const destRegexError = destIsRegex ? validateRegex(destPattern) : undefined;

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.transferLinkRules(group.id) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.transferLinkSuggestions(group.id) }),
    ]);
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        source_account_id: sourceAccountId,
        dest_account_id: destAccountId,
        source_pattern: sourcePattern.trim() || null,
        dest_pattern: destPattern.trim() || null,
        source_pattern_is_regex: sourceIsRegex,
        dest_pattern_is_regex: destIsRegex,
        date_window_days: Number(dateWindow || 1),
        auto_link: autoLink,
      };
      return rule ? financeApi.updateTransferLinkRule(group.id, rule.id, payload) : financeApi.createTransferLinkRule(group.id, payload);
    },
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: () => financeApi.deleteTransferLinkRule(group.id, rule!.id),
    onSuccess: async () => {
      await invalidate();
      onDeleted();
      onClose();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("");
    if (!sourceAccountId || !destAccountId) {
      setStatus("Choose both accounts.");
      return;
    }
    if (sourceAccountId === destAccountId) {
      setStatus("Source and destination accounts must be different.");
      return;
    }
    if (sourceRegexError || destRegexError) {
      setStatus("Fix the invalid regular expression before saving.");
      return;
    }
    saveMutation.mutate();
  };

  return (
    <>
      <SidePanel ariaLabelledBy="ruleModalTitle" className="linked-rule-modal" open={open} onClose={onClose}>
        {({ close }) => (
          <>
            <Box className="records-modal-toolbar">
              <h2 id="ruleModalTitle">{rule ? "Edit rule" : "Add rule"}</h2>
              <IconButton className="records-icon-button" type="button" aria-label="Close" title="Close" onClick={close}>
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </IconButton>
            </Box>
            <form className="records-form linked-rule-form" noValidate onSubmit={submit}>
              <p className="linked-rule-intro">
                A rule pairs a transfer leaving the <strong>source</strong> account with the matching top-up arriving in the{" "}
                <strong>destination</strong> account. Text signatures match against each record's merchant or note — toggle{" "}
                <strong>Regular expression</strong> to match with regex.
              </p>
              <Box className="records-field">
                <AccountSelect
                  accounts={activeAccounts}
                  members={members}
                  emptyLabel="Money leaves this account"
                  label="From · source account"
                  value={sourceAccountId}
                  onChange={setSourceAccountId}
                />
              </Box>
              <Box className="records-field linked-pattern-field-wrap">
                <Box className="linked-pattern-field">
                  <TextField
                    label="Source text signature (optional)"
                    placeholder={sourceIsRegex ? "PURCHASE.*Revolut" : "Revolut"}
                    value={sourcePattern}
                    error={Boolean(sourceRegexError)}
                    helperText={sourceRegexError || "Matches the outgoing record's merchant or note."}
                    fullWidth
                    onChange={(event) => setSourcePattern(event.target.value)}
                  />
                  <Tooltip title={sourceAccountId ? "Fill from an existing record" : "Choose the source account first"}>
                    <span>
                      <Button variant="outlined" type="button" disabled={!sourceAccountId} startIcon={<SearchOutlinedIcon fontSize="small" />} onClick={() => setPicker("source")}>
                        Pick
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                <FormControlLabel
                  className="linked-regex-toggle"
                  control={<Switch size="small" checked={sourceIsRegex} onChange={(event) => setSourceIsRegex(event.target.checked)} />}
                  label="Regular expression"
                />
              </Box>
              <Box className="records-field">
                <AccountSelect
                  accounts={activeAccounts}
                  members={members}
                  emptyLabel="Money arrives in this account"
                  label="Into · destination account"
                  value={destAccountId}
                  onChange={setDestAccountId}
                />
              </Box>
              <Box className="records-field linked-pattern-field-wrap">
                <Box className="linked-pattern-field">
                  <TextField
                    label="Destination text signature (optional)"
                    placeholder={destIsRegex ? "Google Pay top-up.*" : "Google Pay top-up"}
                    value={destPattern}
                    error={Boolean(destRegexError)}
                    helperText={destRegexError || "Matches the incoming record's merchant or note."}
                    fullWidth
                    onChange={(event) => setDestPattern(event.target.value)}
                  />
                  <Tooltip title={destAccountId ? "Fill from an existing record" : "Choose the destination account first"}>
                    <span>
                      <Button variant="outlined" type="button" disabled={!destAccountId} startIcon={<SearchOutlinedIcon fontSize="small" />} onClick={() => setPicker("dest")}>
                        Pick
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                <FormControlLabel
                  className="linked-regex-toggle"
                  control={<Switch size="small" checked={destIsRegex} onChange={(event) => setDestIsRegex(event.target.checked)} />}
                  label="Regular expression"
                />
              </Box>
              <Box className="records-field">
                <TextField
                  label="Date window (days)"
                  type="number"
                  value={dateWindow}
                  slotProps={{ htmlInput: { min: 0, max: 31 } }}
                  helperText="How far apart the two trnsactions can occur."
                  onChange={(event) => setDateWindow(event.target.value)}
                />
              </Box>
              <FormControlLabel
                control={<Switch checked={autoLink} onChange={(event) => setAutoLink(event.target.checked)} />}
                label="Auto-link confident matches - needs both text signatures (Beta - try manual connectionf from sync first)"
              />
              <EditPanelActions
                isEditing={Boolean(rule)}
                submitLabel={rule ? "Save changes" : "Create rule"}
                deleteLabel="Delete rule"
                onDelete={rule ? () => deleteMutation.mutate() : undefined}
                saving={saveMutation.isPending}
                deleting={deleteMutation.isPending}
              />
            </form>
            {status ? <p className="status error">{status}</p> : null}
          </>
        )}
      </SidePanel>

      {picker ? (
        <RecordPatternPicker
          group={group}
          accountId={picker === "source" ? sourceAccountId : destAccountId}
          accountName={accountName(picker === "source" ? sourceAccountId : destAccountId)}
          open
          onPick={(text) => {
            const isRegex = picker === "source" ? sourceIsRegex : destIsRegex;
            const value = isRegex ? escapeRegExp(text) : text;
            if (picker === "source") setSourcePattern(value);
            else setDestPattern(value);
          }}
          onBack={() => setPicker(null)}
          onCloseAll={() => {
            setPicker(null);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}

export function LinkedTransactionsPage({ group, onBlockedAccess }: LinkedTransactionsPageProps) {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<RuleModalState>({ kind: "closed" });
  const [error, setError] = useState("");

  const accountsQuery = useQuery({ queryKey: queryKeys.accounts(group.id), queryFn: () => financeApi.accounts(group.id) });
  const rulesQuery = useQuery({ queryKey: queryKeys.transferLinkRules(group.id), queryFn: () => financeApi.transferLinkRules(group.id) });
  const suggestionsQuery = useQuery({
    queryKey: queryKeys.transferLinkSuggestions(group.id),
    queryFn: () => financeApi.transferLinkSuggestions(group.id),
  });

  useBlockedAccessGuard([accountsQuery.error, rulesQuery.error, suggestionsQuery.error], onBlockedAccess);

  const accounts = accountsQuery.data || [];
  const rules = rulesQuery.data || [];
  const suggestions = suggestionsQuery.data || [];
  const hasRules = rules.length > 0;
  const accountName = useMemo(() => {
    const byId = new Map(accounts.map((account) => [account.id, account.name]));
    return (id: string) => byId.get(id) || "Account";
  }, [accounts]);

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.transferLinkRules(group.id) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.transferLinkSuggestions(group.id) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.records }),
      queryClient.invalidateQueries({ queryKey: queryKeys.summary }),
      queryClient.invalidateQueries({ queryKey: queryKeys.overview(group.id) }),
    ]);
  };

  const scanMutation = useMutation({
    mutationFn: () => financeApi.scanTransferLinkSuggestions(group.id),
    onSuccess: invalidateAll,
    onError: (err) => setError(err instanceof Error ? err.message : "Request failed"),
  });

  const confirmMutation = useMutation({
    mutationFn: (id: string) => financeApi.confirmTransferLinkSuggestion(group.id, id),
    onSuccess: invalidateAll,
    onError: (err) => setError(err instanceof Error ? err.message : "Request failed"),
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => financeApi.dismissTransferLinkSuggestion(group.id, id),
    onSuccess: invalidateAll,
    onError: (err) => setError(err instanceof Error ? err.message : "Request failed"),
  });

  return (
    <>
      <header className="topbar">
        <Box>
          <h1>Linked transactions</h1>
          <p>Pair a transfer leaving one account with the matching top-up arriving in another, so the money is counted once.</p>
        </Box>
      </header>

      <section className="content linked-content">
        <Card className="records-panel">
          <CardContent className="records-panel-content">
            <Box className="records-toolbar">
              <h2 className="linked-section-title">Rules</h2>
              <Button variant="contained" type="button" onClick={() => setModal({ kind: "create" })}>
                Add rule
              </Button>
            </Box>

            {rulesQuery.isLoading ? <p className="status">Loading rules…</p> : null}
            {!rulesQuery.isLoading && !hasRules ? (
              <p className="status">No rules yet. Add a rule to teach the app which transfers to pair, then run a scan.</p>
            ) : null}

            {hasRules ? (
              <div className="settings-table-wrapper">
                <table className="settings-table linked-rules-table">
                  <thead>
                    <tr>
                      <th>From</th>
                      <th></th>
                      <th>Into</th>
                      <th>Match</th>
                      <th>Auto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((rule) => (
                      <tr key={rule.id} className="linked-rule-row" onClick={() => setModal({ kind: "edit", rule })}>
                        <td>
                          <span className="linked-rule-account">
                            <strong>{accountName(rule.source_account_id)}</strong>
                            {rule.source_pattern_is_regex ? (
                              <Tooltip title="This signature is matched as a regular expression, not plain text.">
                                <span className="linked-regex-chip">regex</span>
                              </Tooltip>
                            ) : null}
                          </span>
                          {rule.source_pattern ? (
                            <span className={rule.source_pattern_is_regex ? "linked-rule-pattern mono" : "linked-rule-pattern"}>{rule.source_pattern}</span>
                          ) : null}
                        </td>
                        <td className="linked-rule-arrow">
                          <SwapHorizOutlinedIcon fontSize="small" aria-hidden="true" />
                        </td>
                        <td>
                          <span className="linked-rule-account">
                            <strong>{accountName(rule.dest_account_id)}</strong>
                            {rule.dest_pattern_is_regex ? (
                              <Tooltip title="This signature is matched as a regular expression, not plain text.">
                                <span className="linked-regex-chip">regex</span>
                              </Tooltip>
                            ) : null}
                          </span>
                          {rule.dest_pattern ? (
                            <span className={rule.dest_pattern_is_regex ? "linked-rule-pattern mono" : "linked-rule-pattern"}>{rule.dest_pattern}</span>
                          ) : null}
                        </td>
                        <td>±{rule.date_window_days} day{rule.date_window_days === 1 ? "" : "s"}</td>
                        <td>{rule.auto_link ? "On" : "Off"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="records-panel">
          <CardContent className="records-panel-content">
            <Box className="records-toolbar">
              <h2 className="linked-section-title">
                Suggested links{suggestions.length ? <span className="linked-pending-badge">{suggestions.length} pending</span> : null}
              </h2>
              <Tooltip title={hasRules ? "" : "Add a rule first — scanning uses your rules to find matching pairs."} disableHoverListener={hasRules}>
                <span>
                  <Button
                    variant="outlined"
                    type="button"
                    startIcon={<SyncOutlinedIcon fontSize="small" />}
                    disabled={!hasRules || scanMutation.isPending}
                    onClick={() => scanMutation.mutate()}
                  >
                    {scanMutation.isPending ? "Scanning…" : "Scan now"}
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {!hasRules ? (
              <p className="status">Add at least one rule above, then scan to detect matching transfers.</p>
            ) : (
              <>
                {suggestionsQuery.isLoading ? <p className="status">Loading suggestions…</p> : null}
                {!suggestionsQuery.isLoading && !suggestions.length ? (
                  <p className="status">No suggested links. Run a scan or import statements to detect matching transfers. You can also try to adjust match date</p>
                ) : null}
              </>
            )}

            <Box className="linked-suggestion-list">
              {suggestions.map((suggestion: TransferLinkSuggestion) => (
                <Box className="linked-suggestion-card" key={suggestion.id}>
                  <Box className="linked-suggestion-legs">
                    <LegSummary leg={suggestion.source} accountName={accountName(suggestion.source.account_id)} />
                    <SwapHorizOutlinedIcon className="linked-suggestion-arrow" fontSize="small" aria-hidden="true" />
                    <LegSummary leg={suggestion.dest} accountName={accountName(suggestion.dest.account_id)} />
                  </Box>
                  <Box className="linked-suggestion-actions">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<LinkOutlinedIcon fontSize="small" />}
                      disabled={confirmMutation.isPending}
                      onClick={() => confirmMutation.mutate(suggestion.id)}
                    >
                      Link
                    </Button>
                    <Button variant="outlined" size="small" disabled={dismissMutation.isPending} onClick={() => dismissMutation.mutate(suggestion.id)}>
                      Dismiss
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {error ? <p className="status error">{error}</p> : null}
      </section>

      {modal.kind !== "closed" ? (
        <RuleModal
          key={modal.kind === "edit" ? `edit-${modal.rule.id}` : "create"}
          state={modal}
          group={group}
          accounts={accounts}
          open
          onClose={() => setModal({ kind: "closed" })}
          onDeleted={() => setError("")}
        />
      ) : null}
    </>
  );
}
